/**
 * CSV export helpers — turns grid-shaped rows into a downloadable CSV file.
 *
 * Shared by the report/dashboard/grid views that let a user export their
 * current filtered results (operations dashboard, inventory, prepaid
 * coverage, command log, maintenance report, …) so each one doesn't
 * hand-roll its own quoting/escaping and blob-download plumbing.
 *
 * Contains:
 * - rowsToCsv — column-descriptor + rows -> CSV string
 * - downloadCsv — CSV string -> browser file download
 */

/**
 * Escape a single CSV field: wrap in double quotes and double any
 * embedded quotes. Unconditional quoting sidesteps having to special-case
 * commas/newlines in the value.
 */
function escapeCsvField(value) {
  if (value == null) return '';
  const str = Array.isArray(value) ? value.join(', ') : String(value);
  return `"${str.replace(/"/g, '""')}"`;
}

/**
 * Build a CSV string from column descriptors and row objects.
 *
 * @param {Array<{ field: string, headerName: string, valueGetter?: (row: object) => * }>} columns
 * @param {Array<object>} rows
 * @param {{ bom?: boolean }} [options] - set `bom: true` to prefix a UTF-8 BOM
 *   (helps Excel detect encoding on files with non-ASCII characters).
 * @returns {string} CSV text with `\n` line endings.
 */
export function rowsToCsv(columns, rows, { bom = false } = {}) {
  const header = columns.map(c => escapeCsvField(c.headerName)).join(',');
  const body = rows.map(row =>
    columns.map(c => escapeCsvField(c.valueGetter ? c.valueGetter(row) : row[c.field])).join(','),
  );
  const csv = [header, ...body].join('\n');
  return bom ? `\uFEFF${csv}` : csv;
}

/**
 * Trigger a browser download of a CSV string as a file.
 *
 * @param {string} filename
 * @param {string} csvString
 */
export function downloadCsv(filename, csvString) {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
